import { useEffect, type ReactNode } from 'react';
import { ConfigProvider, App as AntApp, Empty } from 'antd';
import { useSchoolTheme } from '@/themes/use-theme';
import { useThemeStore } from '@/store/theme';
import { themeList } from '@/themes';

/**
 * Wraps the app in AntD `<ConfigProvider>` and applies three side-effects on
 * every theme/density change:
 *
 *   1. `<html data-theme="…" data-density="…">` so `themes/global.css` can
 *      layer per-theme overrides (sider gradient, hover ring, selection,
 *      print rules) outside of AntD's reach.
 *   2. `<html style="--font-heading: …">` so `:where(h1…h5)` selectors in
 *      global.css can adopt the theme's heading family without us listing
 *      every theme explicitly.
 *   3. Lazy-injects a Google Fonts `<link>` per theme that needs one — once,
 *      cached by href, so toggling between themes doesn't re-download fonts.
 *
 * Density (`comfortable | compact`) is independent of the visual theme and
 * drives `<ConfigProvider componentSize>` so AntD's own padding system reacts.
 */
export function ThemeProvider({ children }: { children: ReactNode }) {
  const theme = useSchoolTheme();
  const density = useThemeStore((s) => s.density);

  // Inject the active theme's Google Fonts link once; subsequent injections
  // for the same href are no-ops (the `<head>` lookup catches the existing tag).
  useEffect(() => {
    for (const t of themeList) {
      const href = t.fontStack.googleFontsHref;
      if (!href) continue;
      if (document.head.querySelector(`link[data-school-font="${t.key}"]`)) continue;
      const link = document.createElement('link');
      link.rel = 'stylesheet';
      link.href = href;
      link.dataset.schoolFont = t.key;
      document.head.appendChild(link);
    }
    // Preconnect once (idempotent guard) — quicker first-paint for any theme
    // that requires Google Fonts.
    if (!document.head.querySelector('link[data-school-fonts-preconnect]')) {
      const pc = document.createElement('link');
      pc.rel = 'preconnect';
      pc.href = 'https://fonts.gstatic.com';
      pc.crossOrigin = 'anonymous';
      pc.dataset.schoolFontsPreconnect = 'true';
      document.head.appendChild(pc);
    }
  }, []);

  // Reflect theme + density on <html> so the CSS keyed off data-theme works,
  // and expose --font-heading so the global heading rule doesn't need a row
  // per theme.
  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', theme.bodyAttr);
    root.setAttribute('data-density', density);
    root.style.setProperty('--font-heading', theme.fontStack.heading);
    root.style.setProperty('--font-body', theme.fontStack.body);
  }, [theme.bodyAttr, theme.fontStack.heading, theme.fontStack.body, density]);

  return (
    <ConfigProvider
      theme={theme.config}
      componentSize={density === 'compact' ? 'small' : 'middle'}
      renderEmpty={renderEmpty}
    >
      <AntApp style={{ minHeight: '100vh' }}>{children}</AntApp>
    </ConfigProvider>
  );
}

/**
 * Swap AntD's heavy default empty illustration (the box that reads as a fax
 * machine at small sizes) for the cleaner line-art across every component-level
 * empty — tables with no rows, dropdowns with no options, etc. Compact dropdown
 * surfaces get a tighter image so they don't balloon.
 */
function renderEmpty(componentName?: string) {
  const compact = componentName === 'Select'
    || componentName === 'TreeSelect'
    || componentName === 'Cascader'
    || componentName === 'Transfer'
    || componentName === 'mentions';
  return (
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      styles={{ image: { height: compact ? 36 : 48 } }}
      description={compact ? 'No results' : 'No data'}
    />
  );
}
